import React, { useState, useMemo } from 'react';
import { Participant, CurrentUser, User } from '../types';
import Alert from './ErrorDisplay';

interface ParticipantsPageProps {
  participants: Participant[];
  onParticipantClick: (participantId: number) => void;
  currentUser: CurrentUser;
  users: User[];
}

const ParticipantsPage: React.FC<ParticipantsPageProps> = ({ participants, onParticipantClick, currentUser, users }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStandort, setSelectedStandort] = useState<string>('all');

  const visibleParticipants = useMemo(() => {
    return participants.filter(p => !p.isBlocked);
  }, [participants]);

  const currentUserData = useMemo(() => {
    return users.find(u => u.id === currentUser.id);
  }, [users, currentUser.id]);

  const currentUserParticipant = useMemo(() => {
    if (currentUserData?.participantId) {
      return participants.find(p => p.id === currentUserData.participantId);
    }
    return null;
  }, [currentUserData?.participantId, participants]);

  const standorte = useMemo(() => {
    return [...new Set(visibleParticipants.map(p => p.standort))].sort();
  }, [visibleParticipants]);

  const filteredParticipants = useMemo(() => {
    return visibleParticipants.filter(p => {
      const matchesSearch = searchTerm === '' ||
        p.vorname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.nachname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.standort.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStandort = selectedStandort === 'all' || p.standort === selectedStandort;

      return matchesSearch && matchesStandort;
    });
  }, [visibleParticipants, searchTerm, selectedStandort]);

  if (!visibleParticipants || visibleParticipants.length === 0) {
    return <Alert title="Keine Teilnehmer" message="Für diese Veranstaltung sind keine Teilnehmer registriert." type="info" />;
  }

  return (
    <div className="space-y-4 p-4 bg-gray-50 rounded-lg shadow-2xl border-1 border-gray-200">
      {/* My Profile Button */}
      {currentUserParticipant && (
        <div className="bg-white rounded-lg shadow-sm p-4">
          <button
            onClick={() => onParticipantClick(currentUserParticipant.id)}
            className="w-full bg-pink-600 text-white py-3 rounded-lg font-medium hover:bg-pink-700 transition-colors flex items-center justify-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            Mein Profil
          </button>
        </div>
      )}

      {/* Search and Filter Row */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow-sm p-4">
          <input
            type="text"
            placeholder="Teilnehmer suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
          />
        </div>

        {/* Standort Filter */}
        <div className="bg-white rounded-lg shadow-sm p-4">
          <select
            value={selectedStandort}
            onChange={(e) => setSelectedStandort(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
          >
            <option value="all">Alle Standorte ({visibleParticipants.length})</option>
            {standorte.map(standort => (
              <option key={standort} value={standort}>
                {standort} ({visibleParticipants.filter(p => p.standort === standort).length})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Participants Count */}
      <div className="text-sm text-gray-600 px-2">
        Zeige {filteredParticipants.length} von {visibleParticipants.length} Teilnehmern
      </div>

      {/* Participants List */}
      <div className="grid md:grid-cols-2 gap-3">
        {filteredParticipants.length === 0 ? (
          <div className="md:col-span-2">
            <Alert title="Keine Ergebnisse" message="Keine Teilnehmer entsprechen Ihren Suchkriterien." type="info" />
          </div>
        ) : (
          filteredParticipants.map((participant, index) => (
            <div
              key={`${participant.email}-${index}`}
              className="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow cursor-pointer overflow-hidden"
              onClick={() => onParticipantClick(participant.id)}
            >
              <div className="flex items-start gap-4 min-w-0">
                {/* Profile Image */}
                {participant.profileImage ? (
                  <img
                    src={participant.profileImage}
                    alt={`${participant.vorname} ${participant.nachname}`}
                    className="w-16 h-16 rounded-full object-cover flex-shrink-0 border-1 border-primary"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center text-white text-xl font-bold flex-shrink-0 border-1 border-primary">
                    {participant.vorname.charAt(0)}{participant.nachname.charAt(0)}
                  </div>
                )}

                {/* Participant Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-gray-800">
                    {participant.titel && `${participant.titel} `}
                    {participant.vorname} {participant.nachname}
                  </h3>
                  {participant.position && (
                    <p className="text-sm text-gray-600 mt-1 truncate">{participant.position}</p>
                  )}
                  <div className="mt-2">
                    <div className="flex items-center text-sm text-gray-600 min-w-0">
                      <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="font-medium truncate">{participant.standort}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ParticipantsPage;
